'use client';

import { usePathname } from 'next/navigation';
import { FC, useCallback, useMemo } from 'react';

import { AnalyticsEventName } from '@/api/analytics';
import { Skeleton } from '@/components/Skeleton';
import { TabNavigation } from '@/components/TabNavigation';
import { GameStaticCategories } from '@/helpers/game';
import { Category } from '@/helpers/gameCategories';
import { useAnalytics } from '@/hooks/analytics';
import { useNavigation } from '@/hooks/navigation';
import { useCMSGameCategories } from '@/modules/lobby/hooks';

interface IGameCategoriesProps {
  subCategoryId?: string;
}

export const GameCategories: FC<IGameCategoriesProps> = ({ subCategoryId }) => {
  const pathname = usePathname();
  const { generateLink } = useNavigation();

  const { sendEventToAnalytics } = useAnalytics();

  const { categories } = useCMSGameCategories();

  const currentPageUrl = useMemo(() => {
    if (subCategoryId) {
      return pathname.slice(0, pathname.indexOf('/sub-category'));
    }

    return pathname;
  }, [subCategoryId, pathname]);

  const urls = useMemo(
    () => categories.map(({ href }) => generateLink(href)),
    [categories, generateLink]
  );

  const selectedCategory = useMemo(() => {
    return (
      categories.findIndex(({ defaultCategoryId }, index) => {
        const url = urls[index];

        if (defaultCategoryId === GameStaticCategories.Lobby) {
          return url === currentPageUrl;
        }
        if (currentPageUrl === generateLink('/')) return false;
        return `${currentPageUrl}#gamesSection`.startsWith(url);
      }) || 0
    );
  }, [categories, currentPageUrl, generateLink]);

  const normalizedCategories = useMemo(
    (): Category[] =>
      categories.map(({ id, title, href }) => ({
        id,
        title,
        pathname: href,
      })),
    [categories]
  );

  const handleNavigationItemClick = useCallback(
    (name: string) => {
      sendEventToAnalytics({
        name: AnalyticsEventName.LOBBY__FILTER_GAMES__CATEGORY__CLICK,
        attributes: { additionalData: JSON.stringify({ name }) },
      });
    },
    [sendEventToAnalytics]
  );

  return !normalizedCategories.length ? (
    <Skeleton.Container padding="4px 0px">
      <Skeleton.Rect height={40} />
    </Skeleton.Container>
  ) : (
    <TabNavigation
      navItems={normalizedCategories}
      selectedItem={selectedCategory}
      fullScreen
      withArrows
      handleNavigationItemClick={handleNavigationItemClick}
    />
  );
};
